import React, {useState} from 'react';
import {gql, useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {Box, Typography} from '@mui/material';
import {FeatureBoundary} from '../../../contexts/FeatureBoundary';
import {GlobalPermissionEnum} from '../../../schema-types/globalTypes';
import {useDjangoContext} from '../../../contexts/useAuthContext';
import {Button} from '../../../components/Button/Button';
import {ScrollableModal} from '../../../components/Common/ScrollableModal';
import {getEnvironment} from '../../../utils/envTest';

const GENERATE_MOCK_DATA = gql`
  mutation GenerateMockData($ownerCuroId: String!, $clear: Boolean!) {
    generateMockData(ownerCuroId: $ownerCuroId, clear: $clear) {
      ok
      errors
    }
  }
`;

export function GenerateMockInvitations() {
  const {djangoUser} = useDjangoContext();
  const {enqueueSnackbar} = useSnackbar();
  const [generateMockData, {loading}] = useMutation(GENERATE_MOCK_DATA);
  const [modalOpen, setModalOpen] = useState(false);
  const env = getEnvironment();

  const handleGenerateMockData = async (clear: boolean) => {
    try {
      const result = await generateMockData({
        variables: {
          ownerCuroId: djangoUser.curoId,
          clear,
        },
      });

      if (result.data?.generateMockData?.ok) {
        enqueueSnackbar('Mock data generated successfully', {variant: 'success'});
      } else {
        const errors = result.data?.generateMockData?.errors || ['Unknown error'];
        enqueueSnackbar(`Failed to generate mock data: ${errors.join(', ')}`, {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate mock data', {variant: 'error'});
    } finally {
      setModalOpen(false);
    }
  };

  // Don't render in production
  if (env === 'PROD') {
    return null;
  }

  return (
    <>
      <FeatureBoundary
        features={[GlobalPermissionEnum.can_access_admin_features]}
        showErrorIfDenied={false}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => setModalOpen(true)}
          disabled={loading}
          sx={{mx: 2, my: 1}}
          eventName="generate_mock_data"
        >
          {loading ? 'Generating...' : 'Generate Mock Data'}
        </Button>
      </FeatureBoundary>

      <ScrollableModal open={modalOpen} onClose={() => setModalOpen(false)} size="small">
        <Box p={3}>
          <Typography variant="h6" gutterBottom>
            Generate Mock Data
          </Typography>
          <Typography variant="body1" sx={{mb: 3}}>
            Do you want to clear existing data before generating new mock data?
          </Typography>
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => handleGenerateMockData(false)}
              disabled={loading}
              eventName="generate_mock_data_no_clear"
            >
              {loading ? 'Generating...' : 'Keep Existing Data'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleGenerateMockData(true)}
              disabled={loading}
              eventName="generate_mock_data_clear"
            >
              {loading ? 'Generating...' : 'Clear & Generate'}
            </Button>
          </Box>
        </Box>
      </ScrollableModal>
    </>
  );
}
